import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
// Styles
import Wrapper, { AccordionBody, AccordionHeader, AccordionHeaderLine, AccordionLeftHeaderWrapper } from './styles';
import { ChevronDown, X, Save, File } from 'react-feather';
import Button from '../Button';
import Flex from '../Flex';
import { StyledSystemProps } from '../../../theme/StyledSystemProps';

export interface AccordionProps extends StyledSystemProps {
  allowCollapseExpand?: boolean;
  children: any;
  className?: string;
  copyable?: boolean;
  save?: boolean;
  defaultVisibility?: boolean;
  editable?: boolean;
  expandArrowLeft?: boolean;
  headerBg?: string;
  headerColor?: string;
  onCopy?: () => void;
  onRemove?: () => void;
  onSave?: () => void;
  onToggle?: Function;
  title: string;
  titleDetail?: React.ReactNode;
}

/**
 * @render react
 * @name Accordion component
 * @description Accordion component.
 * @example
 *  <Accordion
 *    title="Test"
 *  >
 *    <p>Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>
 *  </Accordion>
 */

const Accordion: FC<AccordionProps> = ({
  allowCollapseExpand,
  children,
  className,
  copyable,
  save,
  defaultVisibility,
  editable,
  expandArrowLeft,
  onCopy: handleCopy,
  onRemove: handleRemove,
  onSave: handleSave,
  onToggle,
  title,
  titleDetail,
  ...rest
}) => {
  if (allowCollapseExpand === undefined) allowCollapseExpand = true;
  const [visibility, setVisibility] = useState(defaultVisibility || false);

  useEffect(() => {
    if (defaultVisibility) {
      setVisibility(defaultVisibility);
    }
  }, [defaultVisibility]);

  return (
    <Wrapper>
      <AccordionHeader
        bg={rest.headerBg}
        color={rest.headerColor}
        className={classNames('', className, {
          '-open': visibility,
        })}
      >
        <Flex
          direction="row"
          justify="space-between"
          size="1"
          alignItems="center"
          onClick={() => {
            if (allowCollapseExpand) {
              setVisibility(!visibility);
              if (onToggle) {
                onToggle(!visibility);
              }
            }
          }}
          p={expandArrowLeft ? 0 : 3}
        >
          {title && <span className="a-title">{title}</span>}
          {expandArrowLeft && (
            <AccordionLeftHeaderWrapper>
              <span className="c-icon">{allowCollapseExpand && <ChevronDown />}</span>
              {titleDetail}
              <AccordionHeaderLine />
            </AccordionLeftHeaderWrapper>
          )}
          {!expandArrowLeft && (
            <div>
              {titleDetail}
              <span className="c-icon">{allowCollapseExpand && <ChevronDown />}</span>
            </div>
          )}
        </Flex>
        {save && handleSave && (
          <Button
            bg="success"
            icon={<Save />}
            type="button"
            isIcon={true}
            onClick={handleSave}
            mr={1}
            p={0}
            height="30px"
            width="30px"
          />
        )}
        {copyable && handleCopy && (
          <Button
            bg="success"
            icon={<File />}
            type="button"
            isIcon={true}
            onClick={handleCopy}
            mr={1}
            p={0}
            height="30px"
            width="30px"
          />
        )}
        {editable && (
          <Button
            bg="error"
            icon={<X />}
            type="button"
            isIcon={true}
            onClick={handleRemove}
            p={0}
            height="30px"
            width="30px"
          />
        )}
      </AccordionHeader>
      <AccordionBody visibility={visibility ? visibility.toString() : ''}>{children}</AccordionBody>
    </Wrapper>
  );
};

Accordion.defaultProps = {
  editable: false,
  mb: 2,
  onRemove: () => null,
};

export default Accordion;
